import { Button } from '@/components/shadcn/button'
import { cn } from '@/lib/utils'
import { IconV2CopyToClipboardOutline } from '@icons/IconV2CopyToClipboardOutline'
import { IconV2CorrectOutline } from '@icons/IconV2CorrectOutline'
import { useState } from 'react'
import { toast } from 'sonner'

function useCopySyntax(syntax: string) {
  const [isCopied, setIsCopied] = useState(false)

  const copy = () => {
    void navigator.clipboard.writeText(syntax).then(() => {
      setIsCopied(true)
      toast.success('Copied syntax to clipboard')
      window.setTimeout(() => setIsCopied(false), 2000)
    })
  }

  return { isCopied, copy }
}

export function CopySyntaxButton({ syntax, className }: { syntax: string; className?: string }) {
  const { isCopied, copy } = useCopySyntax(syntax)

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn('size-7 shrink-0 text-muted-foreground', className)}
      onClick={copy}
      aria-label="Copy JSX syntax"
      title="Copy JSX syntax"
    >
      {isCopied ? <IconV2CorrectOutline size={14} /> : <IconV2CopyToClipboardOutline size={14} />}
    </Button>
  )
}

export function ButtonExample({
  syntax,
  children,
  className,
}: {
  syntax: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('inline-flex items-center gap-1', className)}>
      {children}
      <CopySyntaxButton syntax={syntax} />
    </div>
  )
}

export function CopyableSyntaxExample({
  syntax,
  children,
  className,
}: {
  syntax: string
  children: React.ReactNode
  className?: string
}) {
  const { isCopied, copy } = useCopySyntax(syntax)

  return (
    <div
      className={cn('group relative inline-flex cursor-copy rounded-md', className)}
      onClick={copy}
      title="Click to copy syntax"
    >
      <div className="pointer-events-none">{children}</div>
      <div className="absolute -right-1 -top-1 hidden rounded-full bg-background p-0.5 text-foreground shadow-sm group-hover:block">
        {isCopied ? <IconV2CorrectOutline size={14} /> : <IconV2CopyToClipboardOutline size={14} />}
      </div>
    </div>
  )
}
